import React from "react";

type Localization = {
  localeString: string; // BCP 47 language tag, e.g "de-AT"
  timezone: string; // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
  titlePerformanceCalculator: string;
  titleHistoric: string;
  dropdownChooseMainFond: string;
  dropdownChooseAdditionalFonds: string;
  checkboxAga: string;
  labelIndexed: string;
  labelInitial: string;
  labelMonthly: string;
  labelDatepicker: string;
  chartTitlePerformance: string;
  chartTitleInvestition: string;
  warningAdjusted: (fund: string, dateFrom: string, dateTo: string) => React.ReactNode;
  warningNotAvailableLifetime: (fund: string, dateFrom: string, dateTo: string) => React.ReactNode;
  infoMaximumFonds: string;
  infoRuntime: string;
  disclaimerIndexed: string;
  disclaimerCalculation: string;
  disclaimerOnlineOrder: string;
  disclaimerTitle: string;
  headerFonds: string;
  headerEndResult: string;
  headerEndResultInclusive: string;
  headerPerformance: string;
  headerOnlineOrder: string;
  ctaButtonBuy: string;
  dropdownPlaceholder: string;
  currencySign: string;
  currencySignRO: string;
  currency: string;
  currencyRO: string;
  depositChartLabel: string;
  toString: string;
  resetButtonLabel: string;
  datePickerStart: string;
  datePickerEnd: string;
  datePickerButtonAriaLabel?: string;
  datePickerSwitchViewButtonAriaLabel?: string;
  datePickerDayViewSwitchingButtonText?: string;
  datePickerMonthViewSwitchingButtonText?: string;
  datePickerYearViewSwitchingButtonText?: string;
};

const localization: Localization[] = [
  {
    localeString: "de-AT", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance-Rechner",
    titleHistoric: "Darstellung der historischen Wertentwicklung",
    dropdownChooseMainFond: "Wählen Sie einen Fonds aus",
    dropdownChooseAdditionalFonds: "Wählen Sie bis zu vier Vergleichsfonds aus",
    checkboxAga: "Ausgabe-Aufschlag einbeziehen",
    labelIndexed: "Indexiert [1]",
    labelInitial: "Einmalerlag",
    labelMonthly: "Monatliche Einzahlung",
    labelDatepicker: "Veranlagungszeitraum",
    chartTitlePerformance: "Wertentwicklung",
    chartTitleInvestition: " Investition gesamt:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Der Veranlagungszeitraum wurde angepasst, da die Daten für ",
      <strong key={1}>{fund}</strong>,
      " von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " verfügbar sind.",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Der Fonds ",
      <strong key={1}>{fund}</strong>,
      " kann in der Berechnung nicht berücksichtigt werden, da dessen Laufzeit von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " beträgt. Bitte passen Sie den Zeitraum an, oder wählen Sie andere Vergleichfonds aus.",
    ],
    infoMaximumFonds: "Maximale Anzahl an Vergleichsfonds wurde ausgewählt.",
    infoRuntime: "Laufzeit kürzer als 10 Jahre.",
    disclaimerIndexed:
      "[1] Jährliche Anpassung der monatlichen Einzahlung (z.B. 2 % – entspricht dem langfristigen Inflationsziel" +
      " der Europäischen Zentralbank)",
    disclaimerCalculation:
      "[2] Die Berechnung der Wertentwicklung erfolgt lt. OeKB Methode. Die Wertentwicklung unterstellt eine" +
      " vollständige Wiederveranlagung der Ausschüttung und berücksichtigt die Verwaltungsgebühr sowie eine" +
      " allfällige erfolgsbezogene Vergütung. Der bei Kauf gegebenenfalls einmalige Ausgabeaufschlag ist in der" +
      ' Darstellung nicht enthalten, außer Sie setzen das "Häkchen" bei "Ausgabeaufschlag berücksichtigen".' +
      "  Allenfalls individuelle transaktionsbezogene oder laufend ertragsmindernde Kosten (z.B. Konto- und Depotgebühren) sowie Steuern sind in der Darstellung nicht berücksichtigt. Die Wertentwicklung der Vergangenheit lässt keine verlässlichen Rückschlüsse auf die zukünftige Entwicklung eines Fonds zu.",
    disclaimerOnlineOrder:
      "[3] Wenn Sie auf Kaufen klicken, werden Sie zu George, dem modernsten Banking Österreichs, weitergeleitet.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fonds",
    headerEndResult: "Endbetrag",
    headerEndResultInclusive: "Ausgabeaufschlag berücksichtigt",
    headerPerformance: "Wertentwicklung gesamt [2]",
    headerOnlineOrder: "Online bei Erste Bank und Sparkassen kaufen [3]",
    ctaButtonBuy: "Kaufen",
    dropdownPlaceholder: "Fonds suchen",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Gesamteinzahlung",
    toString: "bis",
    resetButtonLabel: "Zurücksetzen",
    datePickerStart: "Startdatum",
    datePickerEnd: "Enddatum",
    datePickerButtonAriaLabel:
      "Öffnen Sie den Kalender für die Auswahl des Datums, das ausgewählt Datum ist der",
    datePickerSwitchViewButtonAriaLabel: "Wechseln Sie die Ansicht des Kalenders",
    datePickerDayViewSwitchingButtonText: "Tag-Ansicht",
    datePickerMonthViewSwitchingButtonText: "Monats-Ansicht",
    datePickerYearViewSwitchingButtonText: "Jahres-Ansicht",
  },
  {
    localeString: "en-AT", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because its maturity is from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
      ". Please adjust the period or select other comparison funds.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      "[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of" +
      " the distribution and takes into account the management fee and any performance-related remuneration. The" +
      " one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you" +
      ' check the "Take front-end load into account" box. Any individual transaction-related or ongoing' +
      " income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund's future performance.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "de-DE", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance-Rechner",
    titleHistoric: "Darstellung der historischen Wertentwicklung",
    dropdownChooseMainFond: "Wählen Sie einen Fonds aus",
    dropdownChooseAdditionalFonds: "Wählen Sie bis zu vier Vergleichsfonds aus",
    checkboxAga: "Ausgabe-Aufschlag einbeziehen",
    labelIndexed: "Indexiert [1]",
    labelInitial: "Einmalerlag",
    labelMonthly: "Monatliche Einzahlung",
    labelDatepicker: "Veranlagungszeitraum",
    chartTitlePerformance: "Wertentwicklung",
    chartTitleInvestition: " Investition gesamt:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Der Veranlagungszeitraum wurde angepasst, da die Daten für ",
      <strong key={1}>{fund}</strong>,
      " von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " verfügbar sind.",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Der Fonds ",
      <strong key={1}>{fund}</strong>,
      " kann in der Berechnung nicht berücksichtigt werden, da dessen Laufzeit von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " beträgt. Bitte passen Sie den Zeitraum an, oder wählen Sie andere Vergleichfonds aus.",
    ],
    infoMaximumFonds: "Maximale Anzahl an Vergleichsfonds wurde ausgewählt.",
    infoRuntime: "Laufzeit kürzer als 10 Jahre.",
    disclaimerIndexed:
      "[1] Jährliche Anpassung der monatlichen Einzahlung (z.B. 2 % – entspricht dem langfristigen Inflationsziel" +
      " der Europäischen Zentralbank)",
    disclaimerCalculation:
      '[2] Die Berechnung der Wertentwicklung erfolgt lt. OeKB Methode. Die Wertentwicklung unterstellt eine vollständige Wiederveranlagung der Ausschüttung und berücksichtigt die Verwaltungsgebühr sowie eine allfällige erfolgsbezogene Vergütung. Der bei Kauf gegebenenfalls einmalige Ausgabeaufschlag ist in der Darstellung nicht enthalten, außer Sie setzen das "Häkchen" bei "Ausgabeaufschlag berücksichtigen".  Allenfalls individuelle transaktionsbezogene oder laufend ertragsmindernde Kosten (z.B. Konto- und Depotgebühren) sowie Steuern sind in der Darstellung nicht berücksichtigt. Die Wertentwicklung der Vergangenheit lässt keine verlässlichen Rückschlüsse auf die zukünftige Entwicklung eines Fonds zu.',
    disclaimerOnlineOrder:
      "[3] Wenn Sie auf Kaufen klicken, werden Sie zu George, dem modernsten Banking Österreichs, weitergeleitet.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fonds",
    headerEndResult: "Endbetrag",
    headerEndResultInclusive: "Ausgabeaufschlag berücksichtigt",
    headerPerformance: "Wertentwicklung gesamt [2]",
    headerOnlineOrder: "Online bei Erste Bank und Sparkassen kaufen [3]",
    ctaButtonBuy: "Kaufen",
    dropdownPlaceholder: "Fonds suchen",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Gesamteinzahlung",
    toString: "bis",
    resetButtonLabel: "Zurücksetzen",
    datePickerStart: "Startdatum",
    datePickerEnd: "Enddatum",
    datePickerButtonAriaLabel:
      "Öffnen Sie den Kalender für die Auswahl des Datums, das ausgewählt Datum ist der",
    datePickerSwitchViewButtonAriaLabel: "Wechseln Sie die Ansicht des Kalenders",
    datePickerDayViewSwitchingButtonText: "Tag-Ansicht",
    datePickerMonthViewSwitchingButtonText: "Monats-Ansicht",
    datePickerYearViewSwitchingButtonText: "Jahres-Ansicht",
  },
  {
    localeString: "en-DE", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because its maturity is from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
      ". Please adjust the period or select other comparison funds.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      '[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of the distribution and takes into account the management fee and any performance-related remuneration. The one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you check the "Take front-end load into account" box. Any individual transaction-related or ongoing income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund\'s future performance.',
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "sk-SK", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Kalkulačka výkonnosti",
    titleHistoric: "Zobrazenie historickej výkonnosti",
    dropdownChooseMainFond: "Vyberte si fond",
    dropdownChooseAdditionalFonds: "Vyberte fondy na porovnanie (max. 4)",
    checkboxAga: "Zohľadniť vstupný poplatok",
    labelIndexed: "Indexované [1]",
    labelInitial: "Počiatočná investícia",
    labelMonthly: "Mesačná investícia",
    labelDatepicker: "Investičné obdobie",
    chartTitlePerformance: "Hodnota investície",
    chartTitleInvestition: "Celková investícia:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Investičné obdobie bolo upravené, pretože dáta pre ",
      <strong key={1}>{fund}</strong>,
      " sú k dispozícii iba od ",
      <strong key={2}>{dateFrom}</strong>,
      " do ",
      <strong key={3}>{dateTo}</strong>,
      ".",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Fond ",
      <strong key={1}>{fund}</strong>,
      " nemôže byť do výpočtu zaradený, pretože dáta sú k dispozícii iba od dátumu vytvorenia fondu ",
      <strong key={2}>{dateFrom}</strong>,
      " .Prosím upravte investičné obdobie alebo vyberte iné fondy na porovnanie.",
    ],
    infoMaximumFonds: "Maximálny počet vybraných fondov.",
    infoRuntime:
      "Zvolené investičné obdobie je dlhšie ako doba existencie fondu. Prosím upravte investičné obdobie.",
    disclaimerIndexed: "[1] Každoročné navýšenie výšky mesačnej investície o zadané percento.",
    disclaimerCalculation:
      "[2] Prezentovaná výkonnosť za zvolené investičné obdobie (časovo vážená miera návratnosti) zahŕňa poplatky ktoré sú účtované z majetku fondu. Akékoľvek iné poplatky, ktoré by mohli byť uplatnené pri nákupe, alebo predaji, prípadne ďalšie špecifické náklady, nie sú započítané. Hodnota investície sa môže aj znižovať a nie je zaručená návratnosť pôvodne investovanej sumy. Výnosy z investície do podielových fondov dosiahnuté v minulosti nie sú zárukou budúcich výnosov. ",
    disclaimerOnlineOrder:
      "[3] Ak kliknete na Kúpiť, budete presmerovaný do internetbankingu George.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fond",
    headerEndResult: "Konečná suma",
    headerEndResultInclusive: "Zohľadnený vstupný poplatok",
    headerPerformance: "Celková výkonnosť [2]",
    headerOnlineOrder: "Nakúpiť online v Slovenskej sporiteľni [3]",
    ctaButtonBuy: "Kúpiť",
    dropdownPlaceholder: "Vyhľadať fondy",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Celková investícia",
    toString: "do",
    resetButtonLabel: "VYMAZAŤ NASTAVENIA",
    datePickerStart: "Dátum začiatku",
    datePickerEnd: "Dátum ukončenia",
    datePickerButtonAriaLabel: "Otvorte kalendár pre výber dátumu, vybraný dátum je",
    datePickerSwitchViewButtonAriaLabel: "Prepnúť zobrazenie kalendára",
    datePickerDayViewSwitchingButtonText: "Zobrazenie dňa",
    datePickerMonthViewSwitchingButtonText: "Zobrazenie mesiaca",
    datePickerYearViewSwitchingButtonText: "Zobrazenie roka",
  },
  {
    localeString: "en-SK", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Investment Value",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because data are available only from the fund launch date ",
      <strong key={2}>{dateFrom}</strong>,
      ". Please adjust investment period or select other funds for comparison.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime:
      "Selected investment period is longer than life period of the fund. Please adjust the investment period.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      "[2] Presented performance (time-weighted rate of return) for selected investment period includes fees paid by the fund. Any other fees, that might be applicable on purchase, any individual transaction specific costs or ongoing costs or costs imposed by the credit institution maintaining the clients custody account are not considered. Past performance is neither a reliable indicator nor a guarantee for the future performance of a fund.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to internet banking George.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "ro-RO", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Calculator performanțe istorice",
    titleHistoric: "Prezentarea performanței istorice",
    dropdownChooseMainFond: "Alege fondul de investiții",
    dropdownChooseAdditionalFonds: "",
    checkboxAga: "Include comisionul de subscriere",
    labelIndexed: "Indexat [1]",
    labelInitial: "Suma investită (unică/ inițială)",
    labelMonthly: "Suma investită lunar",
    labelDatepicker: "Alege perioada de investire",
    chartTitlePerformance: "Performanţă",
    chartTitleInvestition: " Suma totală investită:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Perioada de investire a fost ajustată întrucât datele pentru ",
      <strong key={1}>{fund}</strong>,
      " sunt disponibile începând de la ",
      <strong key={2}>{dateFrom}</strong>,
      "  până la. ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "",
    ],
    infoMaximumFonds: "",
    infoRuntime: "Perioadă mai scurtă de 10 ani",
    disclaimerIndexed: "[1] Ajustarea anuală a investiției lunare",
    disclaimerCalculation:
      "[2] 2 Performanța prezentată ia în considerare reinvestirea integrală a sumelor distribuite de către fond și include comisionul de administrare și orice altă remunerație legată de performanță. Comisionul de subscriere, în situația în care acesta se aplică la momentul achiziției titlurilor de participare, nu este inclus în performanța prezentată, cu excepția cazului în care ai bifat caseta „Include comisionul de subscriere”. Orice costuri individuale legate de tranzacționare sau alte costuri (de exemplu, taxe de cont curent și de custodie) și alte taxe (de exemplu, impozitul pe câștig), nu sunt incluse în performanța prezentă. Performanţele anterioare ale fondurilor de investiţii nu reprezintă o garanţie a realizărilor viitoare.",
    disclaimerOnlineOrder:
      "[3] Dacă faci click pe Cumpără online prin George BCR, veți fi redirecționat către pagina de autentificare în George. Dacă nu ai încă un cont George, îl poți deschide 100% online aici (link https://www.bcr.ro/ro/persoane-fizice/digital-banking/cont-online-george) și, apoi, poți achiziționa titluri de participare la fondul de investiții dorit.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fond de investiții",
    headerEndResult: "Valoare finală",
    headerEndResultInclusive: "Suma finală (comision de subscriere luat în considerare)",
    headerPerformance: "Performanţă totală [2]",
    headerOnlineOrder: "Cumpără online prin George BCR [3]",
    ctaButtonBuy: "Cumpără",
    dropdownPlaceholder: "Caută fonduri de investiții",
    currencySign: "€",
    currencySignRO: "RON",
    currency: "EUR",
    currencyRO: "RON",
    depositChartLabel: "Suma totală investită",
    toString: "până la",
    resetButtonLabel: "Revenire",
    datePickerStart: "",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "en-RO", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [],
    infoMaximumFonds: "",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed: "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      "[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of the distribution and takes into account the management fee and any performance-related remuneration. The one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you check the \"Take front-end load into account\" box. Any individual transaction-related or ongoing income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund's future performance.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currencySignRO: "RON",
    currency: "EUR",
    currencyRO: "RON",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "",
    datePickerEnd: "",
    datePickerButtonAriaLabel: "",
    datePickerSwitchViewButtonAriaLabel: "",
    datePickerDayViewSwitchingButtonText: "",
    datePickerMonthViewSwitchingButtonText: "",
    datePickerYearViewSwitchingButtonText: "",
  },
  {
    localeString: "hr-HR", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Kalkulator prinosa",
    titleHistoric: "Prikaz rezultata poslovanja u proteklom razdoblju",
    dropdownChooseMainFond: "Molimo odaberite fond",
    dropdownChooseAdditionalFonds: "Odaberite najviše četiri fonda za usporedbu",
    checkboxAga: "",
    labelIndexed: "Indeksacija",
    labelInitial: "Početni iznos ulaganja",
    labelMonthly: "Mjesečni iznos",
    labelDatepicker: "Investicijsko razdoblje",
    chartTitlePerformance: "Prinos",
    chartTitleInvestition: "Ukupno ulaganje",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Razdoblje ulaganja prilagođeno je zbog toga što su podaci za ",
      <strong key={1}>{fund}</strong>,
      " dostupni od ",
      <strong key={2}>{dateFrom}</strong>,
      "  do. ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Fond ",<strong key={1}>{fund}</strong>," se ne može izračunati jer je njegovo razdoblje trajanja od ",<strong key={2}>{dateFrom}</strong>," do ", <strong key={3}>{dateTo}</strong>," . Molimo prilagodite razdoblje ulaganja ili odaberite drugi investicijski fond za usporedbu.",
    ],
    infoMaximumFonds: "Odabran je maksimalan broj fondova za usporedbu",
    infoRuntime: "Razdoblje kraće od 10 godina",
    disclaimerIndexed: "[1] Godišnje povećanje mjesečnog iznosa ulaganja",
    disclaimerCalculation:
      "[2] Rezultati poslovanja Erste investicijskih fondova ostvareni u proteklim razdobljima ne predstavljaju indikaciju i nemaju nikakav utjecaj na buduće rezultate poslovanja fondova. Mogući su slijedeci rizici ulaganja: rizik promjene cijena financijskih instrumenata, rizik promjene tečaja, rizik promjene kamatnih stopa, kreditni rizik, rizik namire i rizik druge ugovorne strane, rizik promjene poreznih propisa, rizik likvidnosti, rizik  koncentracije imovine i rizik zemlje. Prinos fondova je izražen nakon odbitka svih troškova osim izlaznih naknada.\n" +
      "Prinos od početka godine izračunava se kao postotna promjena cijene Fonda na datum izvještaja u odnosu na cijenu Fonda na kraju prethodne godine. Prinos u proteklih godinu dana predstavlja prinos koji je Fond ostvario u razdoblju od godine dana, a izračunava se kao postotna promjena cijene Fonda u odnosu na cijenu Fonda istog datuma prethodne godine. Isto se odnosi i na prikaz godišnjeg prinosa za svaku kalendarsku godinu posebno. Prinos od osnutka predstavlja prosječni godišnji prinos i izračunava se kao geometrijska sredina dnevnih prinosa ostvarenih od osnutka Fonda, izražena na godišnjoj razini.",
    disclaimerOnlineOrder:
      "[3] Ukoliko odaberete opciju \"Kupite\" preusmjerit ćemo vas na George.",
    disclaimerTitle: "",
    headerFonds: "Fond",
    headerEndResult: "Ukupni iznos ulaganja",
    headerEndResultInclusive: "Ukupan iznos (uključujući ulaznu naknadu)",
    headerPerformance: "Ostvareni prinos [2]",
    headerOnlineOrder: "Kupite udjele putem Georgea [3]",
    ctaButtonBuy: "Kupite",
    dropdownPlaceholder: "Pretraga fondova",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Ukupno ulaganje",
    toString: "do",
    resetButtonLabel: "Obriši unos",
    datePickerStart: "",
    datePickerEnd: "",
    datePickerButtonAriaLabel: "",
    datePickerSwitchViewButtonAriaLabel: "",
    datePickerDayViewSwitchingButtonText: "",
    datePickerMonthViewSwitchingButtonText: "",
    datePickerYearViewSwitchingButtonText: "",
  },
  {
    localeString: "en-HR", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "",
    labelIndexed: "Indexed",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      "  to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",<strong key={1}>{fund}</strong>," cannot be included in the calculation because its maturity is from ",<strong key={2}>{dateFrom}</strong>," to ", <strong key={3}>{dateTo}</strong>,". Please adjust the period or select other comparison funds.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed: "[1] Annual adjustment of the monthly investment",
    disclaimerCalculation:
      "[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of the distribution and takes into account the management fee and any performance-related remuneration. The one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you check the \"Take front-end load into account\" box. Any individual transaction-related or ongoing income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund's future performance.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Final amount (Upfront-fee taken into account)",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currencySignRO: "",
    currency: "EUR",
    currencyRO: "",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "",
    datePickerEnd: "",
    datePickerButtonAriaLabel: "",
    datePickerSwitchViewButtonAriaLabel: "",
    datePickerDayViewSwitchingButtonText: "",
    datePickerMonthViewSwitchingButtonText: "",
    datePickerYearViewSwitchingButtonText: "",
  },
];

export default localization;
export type { Localization };